import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, Mail } from 'lucide-react';

/**
 * Simple Email Verification Success Page
 *
 * This page is shown after the user clicks the email verification link.
 * It does NOT auto-login the user. Instead, it shows a success message
 * and directs them to the login page.
 */
export default function EmailVerificationPage() {
  const [, setLocation] = useLocation();

  // Check for verification success from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const isVerified = urlParams.get('verified') === 'true';

  useEffect(() => {
    // Clean up URL params
    if (isVerified) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [isVerified]);

  const handleGoToLogin = () => {
    setLocation('/login');
  };

  return (
    <div className="min-h-screen bg-hydro-100 flex items-center justify-center p-4 font-kumbh">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-deep-800 mb-2">Email Verified!</h1>
          <p className="text-deep-500 text-lg">Your account is ready to use</p>
        </div>

        <Card className="border-lilac-300 shadow-lg bg-card">
          <CardHeader className="text-center pb-6">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-purple-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-deep-800">
              ✅ Email Verified Successfully!
            </CardTitle>
            <CardDescription className="text-deep-500 text-base mt-4">
              Your email has been verified. You can now log in to access your account and complete
              your profile setup.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            <Alert className="border-lilac-300 bg-lilac-100 mb-6">
              <Mail className="h-4 w-4 text-purple-600" />
              <AlertDescription className="text-deep-700">
                <strong>Next Steps:</strong>
                <ol className="list-decimal list-inside mt-2 space-y-1">
                  <li>Click the button below to go to the login page</li>
                  <li>Sign in with your email and password</li>
                  <li>Complete your profile setup</li>
                  <li>Start tracking your hydration!</li>
                </ol>
              </AlertDescription>
            </Alert>

            <Button
              onClick={handleGoToLogin}
              className="w-full bg-purple-500 hover:bg-purple-600 text-white h-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Go to Login Page
            </Button>

            <div className="mt-6 text-center">
              <p className="text-sm text-deep-400">
                Need help? Contact support if you continue having issues.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
